import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFunctions, httpsCallable } from 'firebase/functions';

type DemoStep = 'idle' | 'email' | 'code' | 'loading';

/**
 * DemoAccessCard Component
 *
 * Self-contained demo verification flow:
 * 1. idle: Shows "Start Free Demo" button
 * 2. email: Email input form with send code button
 * 3. code: 6-digit verification code input
 * 4. Navigate to /demo/:lessonId on success
 *
 * Preserves all Firebase logic from original Landing.tsx:
 * - sendDemoVerificationCode callable
 * - verifyDemoCode callable
 * - Error handling for deadline-exceeded, permission-denied
 */
export function DemoAccessCard() {
  const navigate = useNavigate();
  const [demoStep, setDemoStep] = useState<DemoStep>('idle');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleRequestDemo = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const functions = getFunctions();
      const sendCode = httpsCallable<
        { email: string },
        { success: boolean; message: string }
      >(functions, 'sendDemoVerificationCode');

      await sendCode({ email });
      setDemoStep('code');
    } catch (err: any) {
      console.error('Error sending verification code:', err);
      setError(err.message || 'Failed to send verification code. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);

    try {
      const functions = getFunctions();
      const verifyCode = httpsCallable<
        { email: string; code: string },
        { success: boolean; demoLessonId: string; message: string }
      >(functions, 'verifyDemoCode');

      const result = await verifyCode({ email, code });

      // Navigate to demo lesson
      navigate(`/demo/${result.data.demoLessonId}`, {
        state: { email, isDemo: true },
      });
    } catch (err: any) {
      console.error('Error verifying code:', err);
      const errorMessage = err.code === 'functions/deadline-exceeded'
        ? 'Code has expired. Please request a new one.'
        : err.code === 'functions/permission-denied'
          ? 'Invalid verification code. Please check and try again.'
          : err.message || 'Failed to verify code. Please try again.';
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="landing-card landing-card--demo animate-scaleIn stagger-3">
      {demoStep === 'idle' && (
        <>
          <h3 className="landing-card__title" style={{ textAlign: 'center' }}>
            Try a Free Demo Lesson
          </h3>
          <p className="landing-card__description" style={{ textAlign: 'center' }}>
            Experience our MOD (Maximum Operating Depth) lesson - no account required.
          </p>
          <button
            onClick={() => setDemoStep('email')}
            className="landing-btn landing-btn--primary"
            style={{ width: '100%' }}
          >
            Start Free Demo
          </button>
        </>
      )}

      {demoStep === 'email' && (
        <form onSubmit={handleRequestDemo}>
          <h3 className="landing-card__title" style={{ textAlign: 'center' }}>
            Enter Your Email
          </h3>
          <p className="landing-card__description" style={{ textAlign: 'center' }}>
            We'll send you a 6-digit verification code.
          </p>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            required
            className="landing-input"
            style={{ marginBottom: 'var(--cld-spacing-md)' }}
          />
          {error && (
            <p
              style={{
                color: 'var(--cld-color-warning)',
                marginBottom: 'var(--cld-spacing-sm)',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              {error}
            </p>
          )}
          <div style={{ display: 'flex', gap: 'var(--cld-spacing-sm)' }}>
            <button
              type="button"
              onClick={() => {
                setDemoStep('idle');
                setError(null);
              }}
              className="landing-btn landing-btn--secondary"
              style={{ flex: 1 }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="landing-btn landing-btn--primary"
              style={{ flex: 2 }}
            >
              {isSubmitting ? 'Sending...' : 'Send Code'}
            </button>
          </div>
        </form>
      )}

      {demoStep === 'code' && (
        <form onSubmit={handleVerifyCode}>
          <h3 className="landing-card__title" style={{ textAlign: 'center' }}>
            Enter Verification Code
          </h3>
          <p className="landing-card__description" style={{ textAlign: 'center' }}>
            Check your email for a 6-digit code. It expires in 10 minutes.
          </p>
          <input
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="000000"
            required
            maxLength={6}
            className="landing-input landing-input--code"
            style={{ marginBottom: 'var(--cld-spacing-md)' }}
          />
          {error && (
            <p
              style={{
                color: 'var(--cld-color-warning)',
                marginBottom: 'var(--cld-spacing-sm)',
                fontSize: 'var(--cld-font-size-sm)',
              }}
            >
              {error}
            </p>
          )}
          <div style={{ display: 'flex', gap: 'var(--cld-spacing-sm)' }}>
            <button
              type="button"
              onClick={() => {
                setDemoStep('email');
                setCode('');
                setError(null);
              }}
              className="landing-btn landing-btn--secondary"
              style={{ flex: 1 }}
            >
              Back
            </button>
            <button
              type="submit"
              disabled={isSubmitting || code.length !== 6}
              className="landing-btn landing-btn--primary"
              style={{ flex: 2 }}
            >
              {isSubmitting ? 'Verifying...' : 'Access Demo'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
